import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { useData } from '../../context/DataContext';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog';
import AccessRestricted from '../../components/AccessRestricted';
import { AssetReturnForm, type ReturnableAsset } from '../../components/AssetReturnForm';
import { AlertTriangle, Archive, ArrowLeft, Loader2, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { personArchiveApi } from '../../utils/person-archive-api';
import { ArchiveDependencies, JourneyPickerConfig, formatEventTime } from './ArchiveDependencies';
import { ArchiveTerminationCard } from './ArchiveTerminationCard';
import {
  ArchiveCategory,
  ArchiveCategoryId,
  ArchiveDependencyItem,
  ArchiveDraft,
  ArchiveEvent,
  ArchivePerson,
  ArchiveReplacement,
  ArchiveSelectablePerson
} from '../../types/personArchive';

/** How many hand-overs to run at once during a bulk action. */
const BULK_CONCURRENCY = 4;

/**
 * Run `worker` over every item with a small concurrency cap. Never rejects —
 * items that fail are returned so the caller can report partial success instead
 * of pretending a bulk action was all-or-nothing.
 */
async function resolveMany<T>(
  items: T[],
  worker: (item: T) => Promise<void>,
  onProgress: () => void
): Promise<T[]> {
  const failures: T[] = [];
  let cursor = 0;

  const runner = async () => {
    while (cursor < items.length) {
      const item = items[cursor];
      cursor += 1;
      try {
        await worker(item);
      } catch (error) {
        console.error('Bulk dependency resolution failed for one item:', error);
        failures.push(item);
      } finally {
        onProgress();
      }
    }
  };

  await Promise.all(Array.from({ length: Math.min(BULK_CONCURRENCY, items.length) }, runner));
  return failures;
}

/**
 * Archive Person journey.
 *
 * A page rather than a dialog because the work is long and resumable: the draft
 * (termination details + letter) is auto-saved, while each dependency is
 * resolved for real as soon as it is confirmed — the UI never claims a hand-over
 * happened unless the backend verifies it.
 */
const ArchivePersonPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const ability = useAbility(AbilityContext);
  const { jobRoles, externalPartners } = useData();

  const [person, setPerson] = useState<ArchivePerson | null>(null);
  const [categories, setCategories] = useState<ArchiveCategory[]>([]);
  const [totals, setTotals] = useState<{ items: number; categories: number }>({ items: 0, categories: 0 });
  const [draft, setDraft] = useState<ArchiveDraft | null>(null);
  const [events, setEvents] = useState<ArchiveEvent[]>([]);
  const [activeId, setActiveId] = useState<ArchiveCategoryId | null>(null);

  const [terminationDate, setTerminationDate] = useState('');
  const [terminationReason, setTerminationReason] = useState('');

  const [selectablePeople, setSelectablePeople] = useState<ArchiveSelectablePerson[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  /** Assets queued through the return form (one at a time). */
  const [assetReturnQueue, setAssetReturnQueue] = useState<ArchiveDependencyItem[]>([]);
  const [assetReturnIndex, setAssetReturnIndex] = useState(0);
  const [returnAsset, setReturnAsset] = useState<ReturnableAsset | null>(null);
  const [loadingReturnAsset, setLoadingReturnAsset] = useState(false);

  /** Last values known to be persisted, so auto-save only writes real edits. */
  const committedRef = useRef({ terminationDate: '', terminationReason: '' });
  const saveTimer = useRef<number | null>(null);

  const personFirstName = person ? person.name.split(' ')[0] : 'this person';

  const loadJourney = useCallback(async () => {
    if (!id) return;

    const [personResponse, dependencies, draftResponse] = await Promise.all([
      personArchiveApi.getPerson(id),
      personArchiveApi.getDependencies(id),
      personArchiveApi.getDraft(id)
    ]);

    setPerson(personResponse.user);
    setCategories(dependencies.categories);
    setTotals(dependencies.totals);
    setDraft(draftResponse.draft);
    setEvents(draftResponse.events);

    setActiveId((current) => {
      if (current && dependencies.categories.some((category) => category.id === current)) return current;
      const firstPending = dependencies.categories.find((category) => !category.resolved);
      return (firstPending || dependencies.categories[0])?.id ?? null;
    });

    committedRef.current = {
      terminationDate: draftResponse.draft?.terminationDate || '',
      terminationReason: draftResponse.draft?.terminationReason || ''
    };
    setTerminationDate(committedRef.current.terminationDate);
    setTerminationReason(committedRef.current.terminationReason);
  }, [id]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        await loadJourney();
      } catch (error) {
        if (cancelled) return;
        setLoadError(error instanceof Error ? error.message : 'Could not load this archive journey.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [loadJourney]);

  // Hand-over pickers offer every active person — see listSelectablePeople().
  useEffect(() => {
    const fetchSelectablePeople = async () => {
      try {
        const response = await personArchiveApi.listSelectablePeople();
        setSelectablePeople(response.users || []);
      } catch (error) {
        console.error('Could not load people for the hand-over pickers:', error);
      }
    };

    void fetchSelectablePeople();
  }, []);

  const refreshResolved = useCallback(async () => {
    if (!id) return;
    const [dependencies, draftResponse] = await Promise.all([
      personArchiveApi.getDependencies(id),
      personArchiveApi.getDraft(id)
    ]);
    setCategories(dependencies.categories);
    setTotals(dependencies.totals);
    setDraft(draftResponse.draft);
    setEvents(draftResponse.events);
  }, [id]);

  const persistDraft = useCallback(
    async (fields: { terminationDate?: string | null; terminationReason?: string | null }, options?: { silent?: boolean }) => {
      if (!id) return;
      setSaving(true);
      try {
        const response = await personArchiveApi.saveDraft(id, fields);
        setDraft(response.draft);
        setEvents(response.events);
        setLastSavedAt(new Date());
        // What the server now holds — the auto-save compares against this.
        committedRef.current = {
          terminationDate: response.draft?.terminationDate || '',
          terminationReason: response.draft?.terminationReason || ''
        };
        if (!options?.silent) toast.success('Draft saved');
      } finally {
        setSaving(false);
      }
    },
    [id]
  );

  // Auto-save the termination details — the first real edit is what creates the
  // draft ("Archiving In Progress" on the profile). Comparing against the
  // committed values (rather than a "first run" flag) means merely opening the
  // page never writes, even though StrictMode mounts effects twice.
  useEffect(() => {
    if (loading || !person) return;

    const unchanged =
      terminationDate === committedRef.current.terminationDate &&
      terminationReason === committedRef.current.terminationReason;
    if (unchanged) return;

    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      void persistDraft({ terminationDate, terminationReason }, { silent: true });
    }, 700);

    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, [terminationDate, terminationReason, loading, person, persistDraft]);

  const pickerConfig: JourneyPickerConfig = useMemo(() => {
    // Everyone active and not archived, minus the person being archived. No role
    // allowlist: Field Workers, Team Admins and any tenant-specific role are
    // valid owners too.
    const people = selectablePeople.filter((person) => String(person.id) !== String(id));

    return {
      users: people.map((person) => ({
        value: String(person.id),
        label: `${person.name} (${person.jobRole || 'none'})`
      })),
      raciGroups: [
        {
          heading: 'Users',
          options: people.map((person) => ({
            value: `User-${person.id}`,
            label: person.name,
            subtitle: person.jobRole
          }))
        },
        {
          heading: 'Job Roles',
          options: jobRoles.map((role) => ({ value: `JobRole-${role.id}`, label: `${role.title || role.roleName} (Role)` }))
        },
        {
          heading: 'External Partners',
          options: externalPartners.map((partner) => ({ value: `ExternalPartner-${partner.id}`, label: `${partner.name} (External)` }))
        }
      ],
      // The backend refuses a desk admin without manage:service_desk_team_admin,
      // so only offer people who hold it.
      deskAdmins: people
        .filter((person) => (person.permissions || []).includes('manage:service_desk_team_admin'))
        .map((person) => ({ value: String(person.id), label: person.name }))
    };
  }, [selectablePeople, jobRoles, externalPartners, id]);

  /**
   * Assets are handed back through the register's own return protocol
   * (signatures, photos, condition notes) rather than a bare unassign, so the
   * form takes over here. Everything else resolves inline.
   */
  const openAssetReturn = (items: ArchiveDependencyItem[]) => {
    if (items.length === 0) return;
    setAssetReturnQueue(items);
    setAssetReturnIndex(0);
  };

  const currentReturnItem = assetReturnQueue[assetReturnIndex];

  // Load the asset behind the current hand-over; if it cannot be read (the
  // caller may lack read:asset) fall back to the row's own values so the return
  // can still be completed.
  useEffect(() => {
    if (!currentReturnItem) {
      setReturnAsset(null);
      return;
    }

    const assetId = currentReturnItem.meta.assetId;
    if (!assetId) {
      setReturnAsset(null);
      return;
    }

    let cancelled = false;
    const fetchAsset = async () => {
      setLoadingReturnAsset(true);
      try {
        const asset = await api.get<ReturnableAsset>(`/api/assets/${assetId}`);
        if (!cancelled) setReturnAsset(asset);
      } catch (error) {
        console.error('Could not load the asset for return:', error);
        if (!cancelled) {
          setReturnAsset({
            id: assetId,
            name: currentReturnItem.values[0],
            tag: currentReturnItem.values[1]
          });
        }
      } finally {
        if (!cancelled) setLoadingReturnAsset(false);
      }
    };

    void fetchAsset();
    return () => {
      cancelled = true;
    };
  }, [currentReturnItem]);

  const closeAssetReturn = () => {
    setAssetReturnQueue([]);
    setAssetReturnIndex(0);
  };

  /**
   * The form has already saved the return; log it and move to the next queued
   * asset (bulk returns are one full hand-over per asset).
   */
  const handleAssetReturned = async (assetId: number) => {
    if (id) {
      try {
        await personArchiveApi.recordEvent(id, { category: 'assets', itemId: assetId });
      } catch (error) {
        console.error('Returned, but the change-history entry was not recorded:', error);
        toast.error('Asset returned, but its change-history entry could not be recorded.');
      }
      await refreshResolved();
    }

    if (assetReturnIndex + 1 < assetReturnQueue.length) {
      setAssetReturnIndex(assetReturnIndex + 1);
      return;
    }
    closeAssetReturn();
  };

  const handleResolve = async (
    category: ArchiveCategoryId,
    item: ArchiveDependencyItem,
    replacement?: ArchiveReplacement
  ) => {
    if (category === 'assets') {
      openAssetReturn([item]);
      return;
    }

    if (!id) return;
    try {
      const event = await personArchiveApi.resolve(id, category, item, replacement);
      if (event) toast.success(event.text);
    } catch (error) {
      // The API layer already surfaced the reason (e.g. "still assigned"); make
      // sure the panel reflects reality either way.
      console.error('Failed to resolve dependency:', error);
    } finally {
      await refreshResolved();
    }
  };

  /**
   * Bulk hand-over: the same verified per-item path, run a few at a time. Rows
   * that fail stay in the panel, so the archive stays blocked until they are
   * genuinely resolved — the summary never overstates what happened.
   */
  const handleBulkResolve = async (
    category: ArchiveCategoryId,
    items: ArchiveDependencyItem[],
    replacement?: ArchiveReplacement
  ) => {
    if (!id || items.length === 0) return;

    if (category === 'assets') {
      openAssetReturn(items);
      return;
    }

    setBulkProgress({ done: 0, total: items.length });
    try {
      const failures = await resolveMany(
        items,
        async (item) => {
          await personArchiveApi.resolve(id, category, item, replacement);
        },
        () => setBulkProgress((previous) => (previous ? { ...previous, done: previous.done + 1 } : previous))
      );

      const succeeded = items.length - failures.length;
      if (failures.length === 0) {
        toast.success(`${succeeded} ${succeeded === 1 ? 'item' : 'items'} resolved.`);
      } else {
        toast.error(`${succeeded} of ${items.length} resolved — ${failures.length} still outstanding, see the panel.`);
      }
    } finally {
      await refreshResolved();
      setBulkProgress(null);
    }
  };

  const handleArchive = async () => {
    if (!id || !person) return;
    setSubmitting(true);
    try {
      await personArchiveApi.archivePerson(id, { terminationDate, terminationReason });
      toast.success(`${person.name} archived.`);
      navigate(`/people/${id}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelJourney = async () => {
    if (!id || !person) return;
    setCancelling(true);
    try {
      await personArchiveApi.cancelDraft(id);
      setCancelOpen(false);
      toast.success(`Archiving cancelled. ${personFirstName} remains active.`);
      navigate(`/people/${id}`);
    } finally {
      setCancelling(false);
    }
  };

  if (!ability.can(ACTIONS.ARCHIVE, RESOURCES.PERSON)) {
    return <AccessRestricted />;
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[70vh] space-y-4">
        <Loader2 className="h-10 w-10 animate-spin text-slate-400" />
        <p className="text-slate-500 font-medium animate-pulse text-sm uppercase tracking-widest">Loading archive journey...</p>
      </div>
    );
  }

  if (loadError || !person) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] space-y-4">
        <AlertTriangle size={48} className="text-destructive" />
        <h2 className="text-xl font-semibold">Could not open this archive journey</h2>
        <p className="text-muted-foreground">{loadError || 'This person could not be loaded.'}</p>
        <Button variant="outline" onClick={() => navigate('/people')}>Back to People</Button>
      </div>
    );
  }

  const missing: string[] = [];
  if (!terminationDate) missing.push('termination date');
  if (!terminationReason) missing.push('termination reason');
  if (totals.items > 0) missing.push(`${totals.items} dependency ${totals.items === 1 ? 'item' : 'items'}`);
  const canArchive = missing.length === 0;

  const draftChipLabel = saving
    ? 'Saving draft…'
    : draft && lastSavedAt && Date.now() - lastSavedAt.getTime() < 60_000
    ? 'Draft auto-saved · just now'
    : draft
    ? `Draft auto-saved · ${formatEventTime(draft.savedAt)}`
    : 'Draft not started';

  return (
    <div className="space-y-6 p-2 md:p-0 pb-28">
      <div>
        <button
          type="button"
          onClick={() => navigate(`/people/${id}`)}
          className="mb-3 inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <ArrowLeft size={16} />
          Back to {personFirstName}&apos;s profile
        </button>

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-3xl">
            <h1 className="text-xl font-bold md:text-2xl">Archive {person.name}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Archiving makes this person inactive in Idarah. Their employment history, documents, training records and
              activity log are preserved, and they can be unarchived at any time. Before archiving you must hand over
              everything currently assigned to them.
            </p>
          </div>

          <div className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-700">
            <Archive size={14} />
            <span>{draftChipLabel}</span>
          </div>
        </div>
      </div>

      <ArchiveTerminationCard
        personFirstName={personFirstName}
        personName={person.name}
        terminationDate={terminationDate}
        terminationReason={terminationReason}
        onTerminationDateChange={setTerminationDate}
        onTerminationReasonChange={setTerminationReason}
        attachment={draft?.attachment || null}
        attachmentUrl={draft?.attachment?.url}
        onUpload={async (file) => {
          try {
            const response = await personArchiveApi.uploadAttachment(id as string, file);
            setDraft(response.draft);
            setEvents(response.events);
            toast.success('Attachment uploaded');
          } catch (error) {
            console.error('Attachment upload failed:', error);
          }
        }}
        onRemoveAttachment={async () => {
          const response = await personArchiveApi.removeAttachment(id as string);
          setDraft(response.draft);
          setEvents(response.events);
        }}
        uploading={saving}
        unresolvedItems={totals.items}
        unresolvedCategories={totals.categories}
      />

      <ArchiveDependencies
        categories={categories}
        events={events}
        activeId={activeId}
        onSelect={setActiveId}
        pickerConfig={pickerConfig}
        onResolve={handleResolve}
        onBulkResolve={handleBulkResolve}
        bulkProgress={bulkProgress}
        busy={submitting || cancelling}
      />

      {/* Sticky action bar */}
      <div className="fixed inset-x-0 bottom-0 z-20 border-t bg-background/95 px-3 py-3 backdrop-blur md:static md:rounded-xl md:border md:px-5">
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            className="gap-2 border-destructive/30 text-destructive hover:bg-red-50"
            onClick={() => setCancelOpen(true)}
          >
            <X size={16} />
            Cancel Archiving
          </Button>

          <div className="min-w-[180px] flex-1 text-xs text-muted-foreground">
            {canArchive ? 'Ready to archive.' : `Still required: ${missing.join(', ')}.`}
          </div>

          <Button
            variant="outline"
            className="gap-2"
            onClick={() => void persistDraft({ terminationDate, terminationReason })}
            disabled={saving}
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save Draft
          </Button>

          <Button
            className="gap-2 bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={!canArchive || submitting}
            onClick={handleArchive}
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Archive size={16} />}
            Archive Person
          </Button>
        </div>
      </div>

      {/* Asset return — the register's own protocol, not a bare unassign */}
      <Dialog
        open={assetReturnQueue.length > 0}
        onOpenChange={(open) => {
          if (!open) closeAssetReturn();
        }}
      >
        <DialogContent className="max-h-[92vh] max-w-[880px] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Return Asset</DialogTitle>
            <DialogDescription className="text-sm">
              Complete the return handover recorded against {person.name}&apos;s profile — the same record the Asset
              Register uses. The asset leaves their name and goes back to the pool.
            </DialogDescription>
          </DialogHeader>

          {loadingReturnAsset || !returnAsset ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              <p className="text-xs font-medium uppercase tracking-widest text-slate-500">Loading asset…</p>
            </div>
          ) : (
            <AssetReturnForm
              // Fresh form per asset: each hand-over needs its own receiver and
              // signature, not the previous asset's.
              key={assetReturnIndex}
              asset={returnAsset}
              receivers={selectablePeople}
              targetStatus="Available"
              onCancel={closeAssetReturn}
              onReturned={handleAssetReturned}
              progressLabel={
                assetReturnQueue.length > 1
                  ? `Asset ${assetReturnIndex + 1} of ${assetReturnQueue.length}`
                  : undefined
              }
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-start gap-3 text-lg font-medium">
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-50 text-destructive">
                <AlertTriangle size={20} />
              </span>
              Cancel archiving {person.name}?
            </DialogTitle>
            <DialogDescription className="pt-1 text-[13px]">
              This abandons the archiving process. The termination date, reason and attachment you entered will be
              cleared, and {personFirstName} stays fully active.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3.5 py-3">
            <div className="mb-1.5 text-[13px] font-semibold text-destructive">Dependency changes are NOT reverted</div>
            <p className="text-[13px] text-muted-foreground">
              Anything you already resolved in this attempt has really happened — assets unassigned, business system
              access removed, procedures transferred, and manager / RACI / service desk / yard reassignments. Cancelling
              does not undo them. To restore the previous assignments you must redo each one manually.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)} disabled={cancelling}>
              Keep archiving
            </Button>
            <Button
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleCancelJourney}
              disabled={cancelling}
            >
              {cancelling && <Loader2 size={16} className="mr-2 animate-spin" />}
              Yes, cancel archiving
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArchivePersonPage;
